import React, { useState, useEffect, useContext } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { View, ActivityIndicator, StyleSheet } from "react-native";
import { Session as SupabaseSession } from "@supabase/supabase-js";
import { supabase } from "../utils/supabase";
import { SessionContext } from "../contexts";
import {
  buildSigninPathWithReturnTo,
  setPendingPostAuthRedirectPath,
} from "../utils/authRedirect";

import {
  getClinicSdkKeys,
  getClinicPlan,
  getUserInfo,
  TPlanGetResponse,
} from "../services/api";
import { getClinicInfo, TGetClinicInfoResponse } from "../services/api-clinic";
import { getFirstDailyAnalytics } from "../services/api-analytics";

import OnboardingModalOpener from "../components/common/native/OnboardingModalOpener";
import WidgetDeployCelebrationModal from "../components/common/native/WidgetDeployCelebrationModal";
import { TModal, TPrimaryGoal, TSession } from "../utils/types";
import {
  markWidgetDeployCelebrationSeen,
  shouldShowWidgetDeployCelebration,
} from "../utils/widgetDeployCelebration";

const API_CALL_TIMEOUT_MS = 5000;
type TWidgetDeployCelebrationCandidate = {
  clinicId: string;
  widgetDeployedAt: string;
  deployedHost: string | null;
};

const getTokenSnippet = (token?: string | null) =>
  token ? `${token.slice(0, 6)}...` : "none";

const normalizePrimaryGoal = (goal?: string | null): TPrimaryGoal | null => {
  if (!goal) return null;
  if (
    goal === "lead_capture" ||
    goal === "in_office_simulations" ||
    goal === "clinical_documentation" ||
    goal === "before_after_marketing" ||
    goal === "other"
  ) {
    return goal;
  }
  return null;
};

async function callWithTimeout<T>(
  label: string,
  fn: (signal: AbortSignal) => Promise<T>,
  timeoutMs = API_CALL_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let settled = false;
  console.log(`[SupabaseNavigation] ${label}: start (timeout ${timeoutMs}ms)`);

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      controller.abort();
      console.error(
        `[SupabaseNavigation] ${label}: timed out after ${timeoutMs}ms`,
      );
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    fn(controller.signal)
      .then((value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        console.log(`[SupabaseNavigation] ${label}: success`);
        resolve(value);
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (controller.signal.aborted) {
          console.warn(`[SupabaseNavigation] ${label}: aborted`);
        } else {
          console.error(`[SupabaseNavigation] ${label}: error`, error);
        }
        reject(error);
      });
  });
}

const SupabaseNavigation = () => {
  const sessionContext = useContext(SessionContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isResetPasswordRef = React.useRef<boolean>(false);
  const initInFlightRef = React.useRef(false);
  const lastInitKeyRef = React.useRef<string | null>(null);
  const [
    widgetDeployCelebrationCandidate,
    setWidgetDeployCelebrationCandidate,
  ] = React.useState<TWidgetDeployCelebrationCandidate | null>(null);
  const widgetDeployCelebrationWasShownRef = React.useRef(false);

  // 🌟 Global modal state for onboarding
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<TModal>("OnboardingWelcome");
  const [isInitializing, setIsInitializing] = useState(true);
  const maybeShowWidgetDeployCelebration = React.useCallback(
    (clinicId?: string | null, clinicInfo?: TGetClinicInfoResponse | null) => {
      if (!clinicId) return;
      const widgetDeployedAt = clinicInfo?.widget_deployed_at;
      if (!widgetDeployedAt) return;
      if (widgetDeployCelebrationCandidate) return;
      const shouldShow = shouldShowWidgetDeployCelebration({
        clinicId,
        widgetDeployedAt,
      });
      if (!shouldShow) {
        return;
      }

      setWidgetDeployCelebrationCandidate({
        clinicId,
        widgetDeployedAt,
        deployedHost:
          clinicInfo?.widget_deployed_origin_host ||
          clinicInfo?.widget_deployed_referer_host ||
          null,
      });
      widgetDeployCelebrationWasShownRef.current = false;
    },
    [widgetDeployCelebrationCandidate],
  );
  const handleCloseWidgetDeployCelebration = React.useCallback(() => {
    if (
      widgetDeployCelebrationCandidate &&
      widgetDeployCelebrationWasShownRef.current
    ) {
      markWidgetDeployCelebrationSeen({
        clinicId: widgetDeployCelebrationCandidate.clinicId,
        widgetDeployedAt: widgetDeployCelebrationCandidate.widgetDeployedAt,
      });
    }
    setWidgetDeployCelebrationCandidate(null);
  }, [widgetDeployCelebrationCandidate]);
  const navigateToSigninWithReturnTo = React.useCallback(() => {
    const returnToPath = `${location.pathname}${location.search}${location.hash}`;
    setPendingPostAuthRedirectPath(returnToPath);
    navigate(buildSigninPathWithReturnTo(returnToPath), { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  const refreshSessionDataLight = async (
    session: SupabaseSession,
    currentSession: TSession,
  ) => {
    if (!sessionContext) {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: missing sessionContext, skipping",
      );
      return;
    }

    console.log("[SupabaseNavigation] refreshSessionDataLight: start", {
      userId: currentSession.userId,
      clinicId: currentSession.clinicId || "none",
      token: getTokenSnippet(session.access_token),
    });

    const clinicId = currentSession.clinicId;
    if (!clinicId) {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: missing clinicId, updating token only",
      );
      sessionContext.setSession({
        ...currentSession,
        accessToken: session.access_token,
      });
      return;
    }

    let clinicInfo: TGetClinicInfoResponse | null = null;
    let clinicSdkKeys: string[] | null = null;
    let paymentInfo: TPlanGetResponse | null = null;
    let deployStatus = currentSession.deployStatus ?? false;

    const [
      clinicInfoResult,
      clinicSdkKeysResult,
      paymentInfoResult,
      analyticsResult,
    ] = await Promise.allSettled([
      callWithTimeout("light:getClinicInfo", (signal) =>
        getClinicInfo(session.access_token, clinicId, { signal }),
      ),
      callWithTimeout("light:getClinicSdkKeys", (signal) =>
        getClinicSdkKeys(session.access_token, clinicId, { signal }),
      ),
      callWithTimeout("light:getClinicPlan", (signal) =>
        getClinicPlan(
          {
            accessToken: session.access_token,
            clinicId,
          },
          { signal },
        ),
      ),
      callWithTimeout("light:getFirstDailyAnalytics", (signal) =>
        getFirstDailyAnalytics(
          session.access_token,
          clinicId,
          "onboarding-tier1",
          { signal },
        ),
      ).then(() => true),
    ]);

    if (clinicInfoResult.status === "fulfilled") {
      clinicInfo = clinicInfoResult.value;
    } else {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: getClinicInfo failed",
        clinicInfoResult.reason,
      );
    }

    if (clinicSdkKeysResult.status === "fulfilled") {
      clinicSdkKeys = clinicSdkKeysResult.value?.results || [];
    } else {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: getClinicSdkKeys failed",
        clinicSdkKeysResult.reason,
      );
    }

    if (paymentInfoResult.status === "fulfilled") {
      paymentInfo = paymentInfoResult.value;
    } else {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: getClinicPlan failed",
        paymentInfoResult.reason,
      );
    }

    if (analyticsResult.status === "fulfilled") {
      deployStatus = true;
    } else {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: getFirstDailyAnalytics failed",
        analyticsResult.reason,
      );
    }

    const nextSession: TSession = {
      ...currentSession,
      accessToken: session.access_token,
      planType: paymentInfo?.plan_type ?? currentSession.planType,
      isPaymentDelinquent:
        paymentInfo?.is_payment_delinquent ??
        currentSession.isPaymentDelinquent,
      clinicSdkKeys: clinicSdkKeys ?? currentSession.clinicSdkKeys,
      logoUrl: clinicInfo?.logo_url ?? currentSession.logoUrl,
      logoUrlRectangle:
        clinicInfo?.logo_url_rectangle ?? currentSession.logoUrlRectangle,
      inviteCode: clinicInfo?.invite_code ?? currentSession.inviteCode,
      surgeons:
        clinicInfo?.surgeons?.map((s) => ({
          name: s.name || "",
          photoUrl: s.photo_url || "",
          address: s.address || "",
        })) || currentSession.surgeons,
      primaryGoal:
        normalizePrimaryGoal(clinicInfo?.primary_goal) ??
        currentSession.primaryGoal,
      websiteUrl: clinicInfo?.website_url ?? currentSession.websiteUrl,
      freeTrialEndDate:
        clinicInfo?.free_trial_end_date ?? currentSession.freeTrialEndDate,
      deployStatus,
    };

    maybeShowWidgetDeployCelebration(clinicId, clinicInfo);

    if (!clinicInfo) {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: clinicInfo missing, using cached values",
      );
    }
    if (!clinicSdkKeys || clinicSdkKeys.length === 0) {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: sdk keys missing, using cached values",
      );
    }
    if (!paymentInfo) {
      console.warn(
        "[SupabaseNavigation] refreshSessionDataLight: payment info missing, using cached values",
      );
    }

    sessionContext.setSession(nextSession);
    console.log("[SupabaseNavigation] refreshSessionDataLight: complete");
  };

  const initializeSessionData = async (session: SupabaseSession) => {
    if (!sessionContext) {
      console.warn(
        "[SupabaseNavigation] initializeSessionData: missing sessionContext, aborting initialization",
      );
      setIsInitializing(false);
      return;
    }

    const initKey = `${session.user.id}:${session.access_token}`;
    if (initInFlightRef.current) {
      console.log(
        "[SupabaseNavigation] initializeSessionData: already in flight, skipping",
        { initKey },
      );
      return;
    }
    if (lastInitKeyRef.current === initKey) {
      console.log(
        "[SupabaseNavigation] initializeSessionData: already initialized for token, skipping",
        { initKey },
      );
      setIsInitializing(false);
      return;
    }

    initInFlightRef.current = true;

    console.log(
      "[SupabaseNavigation] initializeSessionData: incoming session",
      {
        userId: session.user.id,
        token: getTokenSnippet(session.access_token),
      },
    );

    try {
      const userInfo = await callWithTimeout("getUserInfo", (signal) =>
        getUserInfo(session.access_token, session.user.id, { signal }),
      );

      if (!userInfo) {
        console.warn(
          "[SupabaseNavigation] initializeSessionData: getUserInfo returned empty result",
        );
        return;
      }

      let clinicInfo: TGetClinicInfoResponse | null = null;
      let clinicSdkKeys: string[] = [];
      let paymentInfo: TPlanGetResponse | null = null;
      const clinicId = userInfo?.clinicId;

      if (clinicId) {
        const [clinicInfoResult, clinicSdkKeysResult, paymentInfoResult] =
          await Promise.allSettled([
            callWithTimeout("getClinicInfo", (signal) =>
              getClinicInfo(session.access_token, clinicId, { signal }),
            ),
            callWithTimeout("getClinicSdkKeys", (signal) =>
              getClinicSdkKeys(session.access_token, clinicId, { signal }),
            ),
            callWithTimeout("getClinicPlan", (signal) =>
              getClinicPlan(
                {
                  accessToken: session.access_token,
                  clinicId,
                },
                { signal },
              ),
            ),
          ]);

        if (clinicInfoResult.status === "fulfilled") {
          clinicInfo = clinicInfoResult.value;
        } else {
          console.warn(
            "[SupabaseNavigation] initializeSessionData: getClinicInfo failed",
            clinicInfoResult.reason,
          );
        }

        if (clinicSdkKeysResult.status === "fulfilled") {
          clinicSdkKeys = clinicSdkKeysResult.value?.results || [];
        } else {
          console.warn(
            "[SupabaseNavigation] initializeSessionData: getClinicSdkKeys failed",
            clinicSdkKeysResult.reason,
          );
        }

        if (paymentInfoResult.status === "fulfilled") {
          paymentInfo = paymentInfoResult.value;
        } else {
          console.warn(
            "[SupabaseNavigation] initializeSessionData: getClinicPlan failed",
            paymentInfoResult.reason,
          );
        }
      } else {
        console.warn(
          "[SupabaseNavigation] initializeSessionData: userInfo missing clinicId, skipping clinic-level requests",
        );
      }

      let deployStatus = false;

      if (clinicId && session?.access_token) {
        try {
          await callWithTimeout("getFirstDailyAnalytics", (signal) =>
            getFirstDailyAnalytics(
              session.access_token,
              clinicId,
              "onboarding-tier1",
              { signal },
            ),
          );
          deployStatus = true;
        } catch (error) {
          console.warn(
            "[SupabaseNavigation] initializeSessionData: getFirstDailyAnalytics failed",
            error,
          );
        }
      } else {
        console.info(
          "[SupabaseNavigation] initializeSessionData: skipping analytics, missing clinicId or token",
        );
      }

      const isPaymentDelinquent =
        paymentInfo?.is_payment_delinquent ??
        sessionContext.session?.isPaymentDelinquent;
      const planTypeFromBilling =
        paymentInfo?.plan_type || userInfo?.planType || "";

      if (!clinicInfo) {
        console.warn(
          "[SupabaseNavigation] initializeSessionData: clinicInfo missing, using defaults",
        );
      }
      if (!clinicSdkKeys.length) {
        console.warn(
          "[SupabaseNavigation] initializeSessionData: sdk keys missing, using defaults",
        );
      }
      if (!paymentInfo) {
        console.warn(
          "[SupabaseNavigation] initializeSessionData: payment info missing, using defaults",
        );
      }

      sessionContext.setSession({
        role: userInfo?.role || "",
        clinicId: userInfo?.clinicId || "",
        clinicName: userInfo?.clinicName || "",
        planType: planTypeFromBilling,
        accessToken: session.access_token,
        userId: session.user.id,
        userEmail: session.user.email || "",
        logoUrl: clinicInfo?.logo_url || "",
        logoUrlRectangle: clinicInfo?.logo_url_rectangle || "",
        deployStatus,
        clinicSdkKeys,
        surgeons:
          clinicInfo?.surgeons.map((s) => ({
            name: s.name || "",
            photoUrl: s.photo_url || "",
            address: s.address || "",
          })) || [],
        showOnboardTipsBeforeAfter:
          userInfo?.showOnboardTipsBeforeAfter || false,
        inviteCode: clinicInfo?.invite_code || "",
        primaryGoal: normalizePrimaryGoal(clinicInfo?.primary_goal),
        websiteUrl: clinicInfo?.website_url || "",
        createdAt: session.user.created_at,
        freeTrialEndDate: clinicInfo?.free_trial_end_date || null,
        isPaymentDelinquent,
      });
      maybeShowWidgetDeployCelebration(clinicId, clinicInfo);
      lastInitKeyRef.current = initKey;
    } catch (error) {
      console.error("Failed to initialize session data:", error);
    } finally {
      initInFlightRef.current = false;
      setIsInitializing(false);
    }
  };

  // Check for existing session on component mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error("[SupabaseNavigation] Error checking session:", error);
          // If there's an auth error (like invalid refresh token), clear session and redirect
          if (
            error.message?.includes("refresh") ||
            error.message?.includes("token")
          ) {
            sessionContext?.setSession(null);
            navigateToSigninWithReturnTo();
          }
          setIsInitializing(false);
          return;
        }

        if (data.session) {
          const currentSession = sessionContext?.session;
          const tokenChanged =
            data.session.access_token !== currentSession?.accessToken;
          const userChanged = currentSession
            ? data.session.user.id !== currentSession.userId
            : true;

          if (!currentSession || tokenChanged || userChanged) {
            await initializeSessionData(data.session);
          } else {
            console.log(
              "[SupabaseNavigation] checkExistingSession: session unchanged, skipping initialization",
            );
            setIsInitializing(false);
          }
        } else {
          setIsInitializing(false);
        }
      } catch (error: any) {
        console.error("[SupabaseNavigation] Error checking session:", error);
        // If it's an auth error, redirect to sign in
        if (
          error?.name === "AuthApiError" ||
          error?.message?.includes("Invalid Refresh Token")
        ) {
          sessionContext?.setSession(null);
          navigateToSigninWithReturnTo();
        }
        setIsInitializing(false);
      }
    };

    checkExistingSession();
  }, [navigateToSigninWithReturnTo, sessionContext?.session]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(
      async (event: any, session: SupabaseSession | null) => {
        const details = {
          event,
          incomingToken: getTokenSnippet(session?.access_token),
          existingToken: getTokenSnippet(sessionContext?.session?.accessToken),
          incomingUserId: session?.user?.id || "none",
          existingUserId: sessionContext?.session?.userId || "none",
        };
        console.log("[SupabaseNavigation] auth state change", details);

        if (event === "PASSWORD_RECOVERY") {
          navigate("/reset-password");
          isResetPasswordRef.current = true;
          setIsInitializing(false);
          return;
        }

        if (event === "INITIAL_SESSION") {
          console.log(
            "[SupabaseNavigation] INITIAL_SESSION: handled by checkExistingSession",
          );
          return;
        }

        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          if (!session) {
            console.warn(
              "[SupabaseNavigation] auth state change: signed in/refreshed but missing session",
            );
            setIsInitializing(false);
            return;
          }

          const currentSession = sessionContext?.session;
          const tokenChanged =
            session.access_token &&
            session.access_token !== currentSession?.accessToken;
          const userChanged = currentSession
            ? session.user.id !== currentSession.userId
            : true;

          if (currentSession && !userChanged && !tokenChanged) {
            console.log(
              "[SupabaseNavigation] auth state change: session unchanged, skipping initialization",
            );
            setIsInitializing(false);
            return;
          }

          if (event === "TOKEN_REFRESHED" && currentSession && !userChanged) {
            if (tokenChanged) {
              console.log(
                "[SupabaseNavigation] TOKEN_REFRESHED: running lightweight refresh",
              );
              await refreshSessionDataLight(session, currentSession);
            } else {
              console.log(
                "[SupabaseNavigation] TOKEN_REFRESHED: token unchanged, skipping refresh",
              );
            }
            setIsInitializing(false);
            return;
          }

          console.log(
            "[SupabaseNavigation] auth state change: starting full session initialization",
            { userChanged, tokenChanged },
          );
          setIsInitializing(true);
          await initializeSessionData(session);
          return;
        }

        if (event === "SIGNED_OUT") {
          sessionContext?.setSession(null);
          setIsInitializing(false);
          navigateToSigninWithReturnTo();
        }
      },
    );

    return () => {
      data.subscription.unsubscribe();
    };
  }, [navigateToSigninWithReturnTo, sessionContext?.session]);

  useEffect(() => {
    if (!sessionContext?.session?.clinicId) return;
    if (sessionContext.session.primaryGoal !== "lead_capture") return;
    if (sessionContext.session.deployStatus) return;
    if (location.pathname !== "/" && location.pathname !== "/home") return;

    const redirectHandledKey = `lead_capture_redirect_done_${sessionContext.session.clinicId}`;
    if (localStorage.getItem(redirectHandledKey) === "true") return;
    localStorage.setItem(redirectHandledKey, "true");
    localStorage.setItem("hasSeenOnboarding", "true");

    const websiteQuery = sessionContext.session.websiteUrl
      ? `?website=${encodeURIComponent(sessionContext.session.websiteUrl)}`
      : "";
    navigate(`/deploy-widget${websiteQuery}`, { replace: true });
  }, [
    location.pathname,
    navigate,
    sessionContext?.session?.clinicId,
    sessionContext?.session?.deployStatus,
    sessionContext?.session?.primaryGoal,
    sessionContext?.session?.websiteUrl,
  ]);

  useEffect(() => {
    if (!sessionContext?.session) return;

    const { showOnboardTipsBeforeAfter } = sessionContext.session;
    if (sessionContext.session.primaryGoal === "lead_capture") {
      localStorage.setItem("hasSeenOnboarding", "true");
      return;
    }

    if (showOnboardTipsBeforeAfter) {
      console.log("Show onboarding modal");
      const hasSeenOnboarding = localStorage.getItem("hasSeenOnboarding");

      if (!hasSeenOnboarding) {
        console.log("Has not seen onboarding, opening modal");
        setModalVisible(true);
        localStorage.setItem("hasSeenOnboarding", "true");
      }
    }
  }, [sessionContext?.session]);

  // Global error handler for unhandled auth errors
  useEffect(() => {
    const handleError = (event: ErrorEvent | PromiseRejectionEvent) => {
      const error = "reason" in event ? event.reason : event.error;

      if (!error) return;

      // Check if it's an auth error
      const isAuthError =
        error?.name === "AuthApiError" ||
        error?.message?.includes("Invalid Refresh Token") ||
        error?.message?.includes("Refresh Token Not Found") ||
        error?.message?.includes("refresh_token");

      if (isAuthError) {
        console.error("[SupabaseNavigation] Auth error detected:", error);
        sessionContext?.setSession(null);
        navigateToSigninWithReturnTo();
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleError);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleError);
    };
  }, [navigateToSigninWithReturnTo, sessionContext]);

  if (isInitializing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00B7F4" />
      </View>
    );
  }

  return (
    <>
      {/* ✅ Always mounted: onboarding modal survives route changes */}
      <OnboardingModalOpener navigate={navigate} />
      <WidgetDeployCelebrationModal
        visible={Boolean(widgetDeployCelebrationCandidate)}
        deployedHost={widgetDeployCelebrationCandidate?.deployedHost}
        onShown={() => {
          widgetDeployCelebrationWasShownRef.current = true;
        }}
        onClose={handleCloseWidgetDeployCelebration}
      />

      {/* ✅ Page outlet */}
      <Outlet />
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
});

export default SupabaseNavigation;
